// Empty-value policy for editable budget amount/qty fields (O-4288).
export const isEmptyBudgetField = (value) => value === '' || value == null;

// A forecast field is still "following" the offer (not independently set) when
// it's empty, zero, or equal to the offer value — editing the offer then carries
// the forecast along (O-4320). Zero counts as following because a new row's
// right_hand_forecast defaults to "0.0"; the tradeoff is that a deliberately-set
// 0 is treated as unset and re-syncs to the offer (rare for a qty). The String()
// compare guards the '5' !== 5 boundary between serialized and typed values.
export const forecastFollowsOffer = (forecastValue, offerValue) =>
  isEmptyBudgetField(forecastValue) ||
  parseFloat(forecastValue) === 0 ||
  String(forecastValue) === String(offerValue);

// Ticket rows derive their offer qty from availability inputs; recompute it and,
// if the forecast qty is still following the offer, carry the new qty into it so
// the forecast tracks availability (O-4320).
export const ticketQtyChanges = (item, field, value) => {
  const pick = (name) => (field === name ? value : item[name]);
  const rightHand = Math.max(
    0,
    Number(pick('available') || 0) - Number(pick('comps') || 0) - Number(pick('kills') || 0),
  );
  const changes = { right_hand: rightHand };
  if (forecastFollowsOffer(item.right_hand_forecast, item.right_hand)) {
    changes.right_hand_forecast = rightHand;
  }
  return changes;
};

// mode 'blur' (commit): an empty field falls back to 0 so nothing is left
// blank once the user tabs away.
//
// any other mode (keystroke editing): an emptied field stays empty so it can
// be cleared and retyped. Coercing '' -> 0 on every keystroke caused the
// "sticky zero" that doubled digits when typing quickly.
//
// normalizeLineItemNumerics still turns any remaining '' into 0 for the save
// payload.
export const normalizeBudgetFieldValue = (value, mode = 'edit') => {
  if (mode === 'blur') return isEmptyBudgetField(value) ? 0 : value;
  return isEmptyBudgetField(value) ? '' : value;
};

// True when a field already holds zero, as a number or a masked "$0.00" / "0%".
// Focus-clearing keys on this so only zeros are ever emptied: the field's
// numeric meaning is unchanged, so nothing the engine reads can move (O-4781).
export const isZeroBudgetField = (value) => {
  if (isEmptyBudgetField(value)) return false;
  return parseFloat(String(value).replace(/[^0-9.-]/g, '')) === 0;
};

export const withoutClearedInheritingQuantities = (params) => {
  const out = { ...params };
  ['right_hand_forecast', 'right_hand_actual'].forEach((field) => {
    if (out[field] === '') delete out[field];
  });
  return out;
};

export const formatAmountForDisplay = (value) => {
  if (isEmptyBudgetField(value)) return value;
  const text = String(value);
  if (!/^-?\d*\.?\d*$/.test(text)) return value;
  const [whole, fraction = ''] = text.split('.');
  if (fraction.length >= 2) return text;
  return `${whole || '0'}.${fraction.padEnd(2, '0')}`;
};
